import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { local_url } from '../Const';
import { EditOrderForm } from "./EditOrderForm";


const StyledCustomerName = styled(Button)(({ theme }) => ({
  fontSize: '16px',
  padding: '6px 12px',
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}));

const FixedTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 'calc(110vh - 300px)',
  overflow: 'auto',
}));

const FixedTableHead = styled(TableHead)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: 1,
  '& th': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  width: '20%',
  textAlign: 'center', // Center align text
  [theme.breakpoints.down('sm')]: {
    padding: '3px',
    fontSize: '12px',
  },
}));

const StyledOrderIdCell = styled(StyledTableCell)(({ theme }) => ({
  paddingLeft: '8px',
  paddingRight: '8px',
  fontWeight: 'bold',
  textAlign: 'center', // Ensure order ID is centered
  [theme.breakpoints.down('sm')]: {
    padding: '3px',
    fontSize: '12px',
  },
}));



const DiversionTable = ({ fromDate, channelId, location, userId, toDate, onActionTaken }) => {
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [places, setPlaces] = useState({});
  const [selectedPlaces, setSelectedPlaces] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFullAddress, setShowFullAddress] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = `${local_url}/api/OrderStatus/GetChangeRequestList/${location}/ALL/${fromDate.toISOString().split('T')[0].replace(/-/g, '')}/${toDate.toISOString().split('T')[0].replace(/-/g, '')}?FromBranchId=ALL`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
        }
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      // Filter out orders with currentApprovalStatus='DN'
      const filteredOrders = data.filter(order => order.currentApprovalStatus !== 'OK' && order.requestedStatus == 'DR');
      setOrders(filteredOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };



  // const handleBranchReallocation = async (orderId, allottee, placeId) => {
  //   try {
  //     const response = await fetch(
  //       `${local_url}/api/SaleOrderList/RedirectDelBranch?SaleOrderId=${orderId}&ToBranchId=${allottee}&NewPlaceId=${placeId}&CreatedUserId=${userId}`,
  //       {
  //         method: 'POST',
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error(`Branch reallocation failed: ${response.statusText}`);
  //     }

  //     const result = await response.json();
  //     return result;
  //   } catch (error) {
  //     console.error('Error in branch reallocation:', error);
  //     throw error;
  //   }
  // };



  const handleStatusUpdate = async (uniqueId, status) => {
    try {
      // Find the corresponding order
      const order = orders.find(o => o.uniqueId === uniqueId);
      if (!order) {
        throw new Error('Order not found');
      }

      // For approval, validate branch and place selection
      if (status === 'OK') {
        if (!order.allottedTo) {
          alert('Please select a branch for reallocation before approving');
          return;
        }

        if (!selectedPlaces[order.saleOrderID]) {
          alert('Please select a place before approving');
          return;
        }
      }

      // Get the allottedTo and placeId values (only needed for approval)
      const allottedTo = status === 'OK' ? order.allottedTo : '';
      const placeId = status === 'OK' ? selectedPlaces[order.saleOrderID] : '';

      // Update the approval status
      const statusResponse = await fetch(
        `${local_url}/api/OrderStatus/UpdateSaleOrderCRApprovalStatus?UniqueRequestId=${uniqueId}&ApprovedByUserId=${userId}&ApprovalStatus=${status}&NewDelBranchId=${allottedTo}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
          },
        }
      );

      if (!statusResponse.ok) {
        throw new Error(`Status update failed: ${statusResponse.statusText}`);
      }

      // If status is 'OK' (approved), proceed with branch reallocation
      // if (status === 'OK' && allottedTo && placeId) {
      //   await handleBranchReallocation(order.saleOrderID, allottedTo, placeId);
      // }

      // Refresh the orders list after all operations are complete
      await fetchOrders();

      // Call the callback to refresh the parent component's order counts
      if (onActionTaken) {
        onActionTaken();
      }

    } catch (error) {
      console.error('Error in status update process:', error);
      alert('Error updating order status. Please try again.');
    }
  };






  const fetchBranches = async (areaId) => {
    try {
      const response = await fetch(`${local_url}/api/StoreByArea/GetStorByArea/${areaId}/LZ`);
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchPlacesForPincode = async (pincode) => {
    if (!pincode || places[pincode]) return; // Don't fetch if we already have the data

    try {
      const response = await fetch(`${local_url}/api/Place/${pincode}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      setPlaces(prevPlaces => ({
        ...prevPlaces,
        [pincode]: data.places || []
      }));
    } catch (error) {
      console.error('Error fetching places for pincode:', pincode, error);
    }
  };


  useEffect(() => {
    if (channelId === 'web' && location) {
      fetchOrders();
      fetchBranches(location);
    }
  }, [channelId, location, fromDate, toDate]);


  const handleAllotChange = (orderId, allottee) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.saleOrderID === orderId ? { ...order, allottedTo: allottee } : order
      )
    );
  };

  const handlePlaceChange = (orderId, placeId) => {
    setSelectedPlaces(prev => ({
      ...prev,
      [orderId]: placeId
    }));
  };


  const handleCustomerClick = async (order) => {
    setSelectedOrder(order);
    setOpenModal(true);
    if (order.pincode) {
      await fetchPlacesForPincode(order.pincode);
    }
  };


  useEffect(() => {
    orders.forEach(order => {
      if (order.pincode) {
        fetchPlacesForPincode(order.pincode);
      }
    });
  }, [orders]);









  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const handleEditClick = (order) => {
    setSelectedOrderForEdit(order);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (editedData) => {
    try {
      const response = await fetch(
        `${local_url}/api/SaleOrderList/UpdateOrder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...editedData,
            updatedBy: userId
          })
        }

      );

      if (response.ok) {
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }

  };


  return (
    <FixedTableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size={isMobile ? "small" : "medium"} stickyHeader>
        <FixedTableHead>
          <TableRow>
            <StyledTableCell sx={{ width: '10%', textAlign: 'center' }}>Order ID</StyledTableCell>
            <StyledTableCell sx={{ width: '5%', textAlign: 'center' }}>Status</StyledTableCell>
            {!isMobile && <StyledTableCell sx={{ width: '25%', textAlign: 'center' }}>Created Date</StyledTableCell>}

            <StyledTableCell sx={{ width: '10%', textAlign: 'center' }}>Currently Alloted</StyledTableCell>
            <StyledTableCell sx={{ width: '15%', textAlign: 'center' }}>Allot To</StyledTableCell>
            <StyledTableCell sx={{ width: '15%', textAlign: 'center' }}>Place</StyledTableCell>
            <StyledTableCell sx={{ width: '20%', textAlign: 'center' }}>Actions</StyledTableCell>
          </TableRow>
        </FixedTableHead>


        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.saleOrderID}>

              <StyledOrderIdCell sx={{ width: '15%', textAlign: 'center' }}>
                <StyledCustomerName onClick={() => handleCustomerClick(order)} size="small">
                  {order.saleOrderID}
                </StyledCustomerName>
              </StyledOrderIdCell>
              <StyledOrderIdCell
                sx={{
                  width: '5%',
                  textAlign: 'center',
                  color: order.requestedStatus === 'DR' || order.requestedStatus === 'CR' ? 'red' : 'black'
                }}
              >
                {order.requestedStatus === 'DR'
                  ? 'Diversion Request'
                  : order.requestedStatus === 'CR'
                    ? 'Cancellation Request'
                    : 'Closed'}
              </StyledOrderIdCell>
              <StyledOrderIdCell sx={{ width: '15%', textAlign: 'center' }}>
  {new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(new Date(order.createdDate))}
</StyledOrderIdCell>

              <StyledOrderIdCell sx={{ width: '15%', textAlign: 'center' }}>
                {order.fromBranchID}
              </StyledOrderIdCell>
              <StyledTableCell sx={{ width: '15%', textAlign: 'center' }}>
                <FormControl variant="outlined" size="small" fullWidth sx={{ maxWidth: '170px' }}>
                  <InputLabel>Allot to</InputLabel>
                  <Select
                    label="Allot to"
                    value={order.allottedTo || ''}
                    onChange={(e) => handleAllotChange(order.saleOrderID, e.target.value)}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.branchID} value={branch.branchID}>
                        {branch.branchName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </StyledTableCell>

              <StyledTableCell sx={{ width: '15%', textAlign: 'center' }}>
                <FormControl variant="outlined" size="small" fullWidth sx={{ maxWidth: '100px' }}>
                  <InputLabel>Place</InputLabel>
                  <Select
                    label="Place"
                    value={selectedPlaces[order.saleOrderID] || ''}
                    onChange={(e) => handlePlaceChange(order.saleOrderID, e.target.value)}
                  >
                    {places[order.pincode]?.map((place) => (
                      <MenuItem key={place.placeId} value={place.placeId}>
                        {place.placeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </StyledTableCell>

              <StyledTableCell sx={{ width: '20%', textAlign: 'center' }}>
                <Grid container spacing={1} justifyContent="center">
                  <Grid item>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleStatusUpdate(order.uniqueId, 'OK')}
                      disabled={!order.allottedTo || !selectedPlaces[order.saleOrderID]} // Disable when either is not selected
                    >
                      Approve
                    </Button>

                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => handleStatusUpdate(order.uniqueId, 'RJ')}
                    >
                      Reject
                    </Button>
                  </Grid>
                  {/* <Grid item>
      <Button
        size="small"
        variant="contained"
        color="warning"
        onClick={() => handleStatusUpdate(order.uniqueId, 'WH')}
      >
        Hold
      </Button>
    </Grid> */}
                </Grid>
              </StyledTableCell>


            </TableRow>
          ))}
        </TableBody>
      </Table>


      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <>
              <Typography variant="subtitle1">Order ID: {selectedOrder.saleOrderID}</Typography>
              <Typography variant="subtitle1">Customer Name: {selectedOrder.customerName}</Typography>
              <Typography variant="subtitle1">Current Allocated: {selectedOrder.fromBranchID}</Typography>
              <Typography variant="subtitle1">Mobile Number: {selectedOrder.customerMobile}</Typography>
              <Typography variant="subtitle1">Delivery Schedule Date: {selectedOrder.deliveryScheduleDate}</Typography>
              <Typography variant="subtitle1">Time Slot: {selectedOrder.timeSlotName}</Typography>
              <Typography variant="subtitle1">Address: {selectedOrder.customerAddress || 'N/A'}</Typography>
              <Typography variant="subtitle1">Pincode: {selectedOrder.pincode || 'N/A'}</Typography>
              <Typography variant="subtitle1">Place: {selectedOrder.placeName || 'N/A'}</Typography>
              <Typography variant="subtitle1">Delivery Date: {new Date(selectedOrder.deliveryScheduleDate).toLocaleString()}</Typography>
              <Typography variant="subtitle1">Selected Place: {
                places[selectedOrder.pincode]?.find(
                  place => place.placeId === selectedPlaces[selectedOrder.saleOrderID]
                )?.placeName || 'Not Selected'
              }</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {selectedOrderForEdit && (
        <EditOrderForm
          order={selectedOrderForEdit}
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedOrderForEdit(null);
          }}
          onSave={handleSaveEdit}
          branches={branches}
          places={places}
        />
      )}
    </FixedTableContainer>
  );
};

export default DiversionTable;